import React, { useState } from 'react';
import { Camera } from 'lucide-react';

/**
 * ImagePlaceholder
 *
 * Renders a real <img> from /images/{code}.{ext}. If the file doesn't
 * exist yet, falls back to a clearly-labelled placeholder showing the
 * image code, expected dimensions and a short brief.
 *
 * Every placeholder shows a persistent corner badge with the image code,
 * so even tiny placeholders (avatars, thumbnails) remain identifiable.
 *
 * To go live: drop a file named `<code>.<ext>` (e.g. IMG-001.jpg) into
 *   /app/frontend/public/images/
 * and it appears automatically.
 */
const ImagePlaceholder = ({
  code,
  ext = 'png',           // 'png' for screenshots, 'jpg' for photos
  alt,
  aspect = '16 / 10',    // CSS aspect-ratio string
  brief,                 // short description shown in the placeholder
  spec,                  // e.g. "2400×1500 · PNG · <500KB"
  rounded = 'rounded-2xl',
  dark = false,          // dark variant for use on dark backgrounds
  className = '',
}) => {
  const [errored, setErrored] = useState(false);
  const src = `/images/${code}.${ext}`;

  if (!errored) {
    return (
      <img
        src={src}
        alt={alt || code}
        onError={() => setErrored(true)}
        style={{ aspectRatio: aspect }}
        className={`w-full h-auto object-cover ${rounded} ${className}`}
        data-testid={`img-${code.toLowerCase()}`}
      />
    );
  }

  const bg = dark
    ? 'bg-slate-900 border-slate-800 text-slate-200'
    : 'bg-slate-50 border-slate-200 text-slate-600';
  const badgeBg = dark
    ? 'bg-blue-500/15 border-blue-500/30 text-blue-300'
    : 'bg-blue-50 border-blue-100 text-blue-700';

  return (
    <div
      style={{ aspectRatio: aspect }}
      className={`relative w-full border border-dashed ${bg} ${rounded} ${className} overflow-hidden flex flex-col items-center justify-center text-center`}
      data-testid={`img-placeholder-${code.toLowerCase()}`}
      title={`Image needed: ${code} — ${brief || ''}`}
    >
      {/* Background dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: dark
            ? 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
          backgroundSize: '16px 16px',
        }}
      />

      {/* Always-visible corner code badge — readable even on tiny placeholders */}
      <span
        className={`absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded font-mono text-[9px] sm:text-[10px] font-bold tracking-tight leading-none ${
          dark ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'
        }`}
        data-testid={`img-code-${code.toLowerCase()}`}
      >
        {code}
      </span>

      {/* Centered full-size details — visible on medium / large placeholders */}
      <div className="relative px-3 py-4 max-w-full">
        <div
          className={`hidden xs:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1 rounded-full border ${badgeBg} mb-3`}
        >
          <Camera className="w-3 h-3" /> Image needed
        </div>
        <p
          className={`text-xs sm:text-base md:text-2xl lg:text-3xl font-mono font-semibold tracking-tight mb-1 sm:mb-2 ${
            dark ? 'text-white' : 'text-slate-900'
          }`}
        >
          {code}
        </p>
        {brief && (
          <p
            className={`hidden sm:block text-xs md:text-sm max-w-md mx-auto leading-snug ${
              dark ? 'text-slate-400' : 'text-slate-600'
            }`}
          >
            {brief}
          </p>
        )}
        {spec && (
          <p
            className={`hidden md:block mt-2 text-[10px] md:text-[11px] font-mono tracking-tight ${
              dark ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            {spec}
          </p>
        )}
        <p
          className={`hidden md:block mt-3 text-[10px] md:text-[11px] ${
            dark ? 'text-slate-500' : 'text-slate-400'
          }`}
        >
          Save to <code className="font-mono">/public/images/{code}.{ext}</code>
        </p>
      </div>
    </div>
  );
};

export default ImagePlaceholder;
